/**
 * Epic #228 P5: the shared destination contract of the Admin
 * `DestinationEditor`. Admin only (it names Admin locale keys), so it is
 * exported from `@dukanti/shared/admin`, never from the public entry.
 *
 * It sits on top of `slots.ts` and the `external-url.ts` leaf; neither
 * imports it (PD14).
 */
import type {
  StorefrontDestination,
  WebsiteDestinationKind,
  WebsiteIssueCode,
  WebsiteStorePageLink,
} from '../../types/website-templates';
import { explainSocialUrl, type ExternalUrlRejection } from './external-url';
import { isRecord } from './internal';
import { validateWebsiteLinkValue } from './slots';

export type StorefrontDestinationValidation =
  | { readonly ok: true; readonly value: StorefrontDestination }
  | {
      readonly ok: false;
      readonly code: WebsiteIssueCode;
      /** Client-only: why an `external` URL is refused (editor copy). */
      readonly reason?: ExternalUrlRejection;
    };

/**
 * The slot validator's answer for one destination (same codes as
 * `validateSlotValue`, PD2), plus the URL reason when an allowed `external`
 * value is refused for its URL.
 */
export function validateStorefrontDestination(
  value: unknown,
  allowedKinds: readonly WebsiteDestinationKind[],
): StorefrontDestinationValidation {
  const result = validateWebsiteLinkValue(value, allowedKinds);
  if (result.ok) return result;
  if (
    result.code === 'invalid_slot' &&
    isRecord(value) &&
    value.kind === 'external' &&
    allowedKinds.includes('external') &&
    typeof value.url === 'string'
  ) {
    const explained = explainSocialUrl(value.url);
    if (!explained.ok) return { ok: false, code: result.code, reason: explained.reason };
  }
  return { ok: false, code: result.code };
}

const SUMMARY = 'admin.destination.summary';

const STORE_PAGE_SUMMARY: Readonly<Record<Exclude<WebsiteStorePageLink, 'sales'>, string>> = {
  home: `${SUMMARY}.storePage.home`,
  catalog: `${SUMMARY}.storePage.catalog`,
  shop: `${SUMMARY}.storePage.shop`,
  'store-info': `${SUMMARY}.storePage.storeInfo`,
};

/** Admin locale key of the one-line summary of a destination ("Category: …"). */
export function destinationSummaryKey(value: StorefrontDestination): string {
  switch (value.kind) {
    case 'none':
      return `${SUMMARY}.noLink`;
    case 'category':
      return `${SUMMARY}.category`;
    case 'product':
      return `${SUMMARY}.product`;
    case 'custom-collection':
      return `${SUMMARY}.collection`;
    case 'store-page':
      return value.page === 'sales' ? `${SUMMARY}.sales` : STORE_PAGE_SUMMARY[value.page];
    case 'contact':
      return `${SUMMARY}.contact.${value.method}`;
    case 'external':
      return `${SUMMARY}.external`;
  }
}
